import { useMutation, useQuery } from '@tanstack/react-query-v4';
import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  PassAtKCreateRunRequest,
  PassAtKRunConfigDTO,
  PassAtKRunProgressDTO,
  PassAtKRunStatus,
} from '@scale/shared/types/passAtK';
import { ModelEvalEndpointDTO } from '@scale/customer-app-shared';

export function useCreatePassAtKRun() {
  return useMutation<
    PassAtKRunConfigDTO,
    string,
    { projectId: string; config: PassAtKCreateRunRequest }
  >({
    mutationFn: async ({ projectId, config }) => {
      const result = await apiFetch<PassAtKRunConfigDTO>(
        null,
        `corp-api/pass-at-k/${projectId}/create-run`,
        {
          method: 'POST',
          body: config,
        },
      );
      return result;
    },
  });
}

export function getPassAtKRunConfigsQueryKey(projectId: string) {
  return ['passAtKRunConfigs', projectId];
}
export function useGetPassAtKRunConfigs({ projectId }: { projectId: string }) {
  return useQuery<PassAtKRunConfigDTO[]>({
    queryKey: getPassAtKRunConfigsQueryKey(projectId),
    queryFn: async () => {
      return await apiFetch(null, `corp-api/pass-at-k/${projectId}/configs`);
    },
    staleTime: 1000 * 60 * 10,
    refetchInterval: 1000 * 60 * 2,
  });
}

export function useCancelPassAtKRun() {
  return useMutation<{ message: string }, string, { projectId: string; configId: string }>({
    mutationFn: async ({ projectId, configId }) => {
      const result = await apiFetch<{ message: string }>(
        null,
        `corp-api/pass-at-k/${projectId}/cancel-run`,
        {
          method: 'POST',
          body: {
            configId,
          },
        },
      );
      return result;
    },
  });
}

export function useRetryPassAtKRun() {
  return useMutation<
    { message: string },
    string,
    { projectId: string; runId: string; resetRun: boolean; allowedRunStatuses: PassAtKRunStatus[] }
  >({
    mutationFn: async ({ projectId, runId, resetRun, allowedRunStatuses }) => {
      const result = await apiFetch<{ message: string }>(
        null,
        `corp-api/pass-at-k/${projectId}/retry-run`,
        {
          method: 'POST',
          body: {
            runId,
            resetRun,
            allowedRunStatuses,
          },
        },
      );
      return result;
    },
  });
}

export function getRunProgressesQueryKey(projectId: string, runIds: string[]) {
  return ['runProgresses', projectId, JSON.stringify(runIds)];
}

export function useGetRunProgresses({
  projectId,
  runIds,
}: {
  projectId: string;
  runIds: string[];
}) {
  return useQuery<PassAtKRunProgressDTO[]>({
    queryKey: getRunProgressesQueryKey(projectId, runIds),
    queryFn: async () => {
      return await apiFetch(null, `corp-api/pass-at-k/${projectId}/run-progresses`, {
        method: 'POST',
        body: {
          runIds,
        },
      });
    },
    refetchInterval: 1000 * 60 * 2,
  });
}

export function useGetOrCreateModelVersionConfiguration() {
  return useMutation({
    mutationFn: async ({
      modelVersionConfiguration,
      customerId,
    }: {
      modelVersionConfiguration: {
        name: string;
        url: string;
        params: Record<string, any>;
        isCustomerVisible?: boolean;
      };
      customerId: string;
    }) => {
      const result = await apiFetch<ModelEvalEndpointDTO>(
        null,
        `/internal/self_serve/model_manager/model_configuration/get_or_create?spoof=${customerId}`,
        {
          method: 'POST',
          body: {
            ...modelVersionConfiguration,
          },
        },
      );
      return result;
    },
  });
}

export function useGetDefaultLitellmConfig() {
  return useQuery<string>({
    queryKey: ['passAtKRunConfigs', 'defaultLitellmConfig'],
    queryFn: async () => {
      return await apiFetch(null, `corp-api/pass-at-k/default-litellm-api-key`);
    },
  });
}

function getPassAtKRunConfigByRunIdQueryKey(runId: string) {
  return ['passAtKRunConfig', runId];
}
export function useGetPassAtKRunConfigByRunId({
  runId,
  enabled = true,
}: {
  runId: string;
  enabled?: boolean;
}) {
  return useQuery<PassAtKRunConfigDTO>({
    queryKey: getPassAtKRunConfigByRunIdQueryKey(runId),
    queryFn: async () => {
      return await apiFetch(null, `corp-api/pass-at-k/run/${runId}/config`);
    },
    staleTime: 1000 * 60 * 10,
    refetchInterval: 1000 * 60 * 2,
    enabled,
  });
}
